// src/pages/admin/AdminStatsDashboard.jsx
import { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  Divider,
  Button,
  Chip,
} from "@mui/material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
// import api, { getUserMessage } from "../../api/axios";
import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../../utils/pageStyles";
import api, { getUserMessage } from "../../api/axios";

const numberFmt = (n) => (n ?? 0).toLocaleString();
const daysOptions = [7, 14, 30, 60, 90];
const statusOptions = [
  "approved",
  "running",
  "ongoing",
  "started",
  "completed",
  "cancelled",
];

// couleurs du camembert complétées vs annulées
const PIE_COLORS = ["#22c55e", "#ef4444", "#6b7280"];

export default function AdminStatsDashboard() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [days, setDays] = useState(30);
  const [labId, setLabId] = useState("");
  const [userFilter, setUserFilter] = useState(""); // ex: user_id ou username selon ton backend
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cacheBadge, setCacheBadge] = useState("");

  const [labsOptions, setLabsOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [topLabs, setTopLabs] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [resStats, setResStats] = useState(null); // stats réservations globales (durées + crédits)

  // --- Revenue & Realtime (nouveaux) ---
  const [revenue, setRevenue] = useState(null);       // /admin/stats/revenue
  const [realtime, setRealtime] = useState(null);     // /admin/stats/realtime (polling 30s)
  const [revenueLoading, setRevenueLoading] = useState(false);

  const baseURL = api?.defaults?.baseURL || "/api/v1";

  const filtersActive = !!(
    labId ||
    userFilter ||
    statusFilter ||
    dateFrom ||
    dateTo
  );

  // Chargement des labs pour le select
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/labs");
        if (!mounted) return;
        const data = r.data;
        const items = Array.isArray(data)
          ? data
          : data.items || data.results || data.data || [];
        setLabsOptions(items);
      } catch (e) {
        console.error("ERROR /labs (AdminStatsDashboard):", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Chargement des stats admin
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    const labParam = labId ? `&lab_id=${labId}` : "";
    const stParam = statusFilter
      ? `&status=${encodeURIComponent(statusFilter)}`
      : "";
    const userParam = userFilter ? `&user=${encodeURIComponent(userFilter)}` : "";
    const dfParam = dateFrom ? `&date_from=${dateFrom}` : "";
    const dtParam = dateTo ? `&date_to=${dateTo}` : "";

    (async () => {
      try {
        const ov = await api.get(`/admin/stats/overview`);
        if (!mounted) return;
        setOverview(ov.data || null);
        setCacheBadge(ov?.headers?.["x-cache"] || "");

        const [ts, tl, tu, ra, rs] = await Promise.all([
          api.get(
            `/admin/stats/timeseries?days=${days}${labParam}${stParam}${userParam}${dfParam}${dtParam}`
          ),
          api.get(
            `/admin/stats/top-labs?days=${days}&limit=10${labParam}${stParam}${userParam}${dfParam}${dtParam}`
          ),
          api.get(
            `/admin/stats/top-users?days=${days}&limit=10${labParam}${stParam}${dfParam}${dtParam}`
          ),
          api.get(
            `/admin/stats/recent-activity?limit=30&offset=0${labParam}${stParam}${userParam}${dfParam}${dtParam}`
          ),
          api.get(
            `/admin/reservations/stats?days=${days}${labParam}${stParam}${userParam}${dfParam}${dtParam}`
          ),
        ]);

        setSeries(ts.data?.items || []);
        setTopLabs(tl.data?.items || []);
        setTopUsers(tu.data?.items || []);
        setActivity(ra.data?.items || []);
        setResStats(rs.data || null);

        // Revenue WooCommerce (indépendant des filtres labs)
        try {
          setRevenueLoading(true);
          const rev = await api.get(
            `/admin/stats/revenue?days=${days}${dfParam}${dtParam}`
          );
          setRevenue(rev.data || null);
        } catch (revErr) {
          console.warn("Revenue endpoint unavailable:", revErr?.response?.status);
        } finally {
          setRevenueLoading(false);
        }
      } catch (e) {
        if (!mounted) return;
        console.error("ERROR admin stats:", e);
        setError(getUserMessage(e, t("common.unknownError")));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [days, labId, userFilter, statusFilter, dateFrom, dateTo, t]);

  // --- Polling realtime toutes les 30s ---
  useEffect(() => {
    let mounted = true;
    const fetchRealtime = async () => {
      try {
        const rt = await api.get("/admin/stats/realtime");
        if (mounted) setRealtime(rt.data || null);
      } catch (e) {
        // silently ignore — endpoint optionnel
      }
    };
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 30_000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  const kpi = overview?.kpi || {};

  const handleExport = async () => {
    try {
      const labParam = labId ? `&lab_id=${labId}` : "";
      const stParam = statusFilter
        ? `&status=${encodeURIComponent(statusFilter)}`
        : "";
      const userParam = userFilter
        ? `&user=${encodeURIComponent(userFilter)}`
        : "";
      const dfParam = dateFrom ? `&date_from=${dateFrom}` : "";
      const dtParam = dateTo ? `&date_to=${dateTo}` : "";

      const url = `/admin/stats/export.csv?days=${days}${labParam}${stParam}${userParam}${dfParam}${dtParam}`;

      // ⚠️ Très important : on passe par axios pour envoyer le JWT
      const response = await api.get(url, {
        responseType: "blob", // on récupère un vrai fichier
      });

      // Création d’un blob + lien de téléchargement
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "admin_stats_export.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error("Export CSV error:", e);
      setError(getUserMessage(e, t("common.unknownError")));
    }
  };
    
/*
  const handleExport = () => {
    const labParam = labId ? `&lab_id=${labId}` : "";
    const stParam = statusFilter
      ? `&status=${encodeURIComponent(statusFilter)}`
      : "";
    const userParam = userFilter ? `&user=${encodeURIComponent(userFilter)}` : "";
    const dfParam = dateFrom ? `&date_from=${dateFrom}` : "";
    const dtParam = dateTo ? `&date_to=${dateTo}` : "";
    const url = `${baseURL}/admin/stats/export.csv?days=${days}${labParam}${stParam}${userParam}${dfParam}${dtParam}`;
    window.open(url, "_blank");
  };
*/
  // Données Pie complétées vs annulées (en nombre de réservations)
  const completionPieData = (() => {
    if (!resStats) return [];
    const total = resStats.total_reservations || 0;
    const completed = resStats.total_completed || 0;
    const cancelled = resStats.total_cancelled || 0;
    const others = Math.max(total - completed - cancelled, 0);

    const data = [];
    if (completed > 0) {
      data.push({
        name: t("adminStats.pie.completed", { defaultValue: "Completed" }),
        value: completed,
        key: "completed",
      });
    }
    if (cancelled > 0) {
      data.push({
        name: t("adminStats.pie.cancelled", { defaultValue: "Cancelled" }),
        value: cancelled,
        key: "cancelled",
      });
    }
    if (others > 0) {
      data.push({
        name: t("adminStats.pie.other", { defaultValue: "Other statuses" }),
        value: others,
        key: "other",
      });
    }
    return data;
  })();

  const completionRate =
    resStats && resStats.total_reservations
      ? (100 * (resStats.total_completed || 0)) /
        resStats.total_reservations
      : 0;

  // Durées complétées vs annulées
  const durationBarData = (() => {
    if (!resStats) return [];
    return [
      {
        key: "completed",
        name: t("adminStats.duration.completed", {
          defaultValue: "Completed (min)",
        }),
        minutes: resStats.completed_duration_minutes || 0,
      },
      {
        key: "cancelled",
        name: t("adminStats.duration.cancelled", {
          defaultValue: "Cancelled (min)",
        }),
        minutes: resStats.cancelled_duration_minutes || 0,
      },
    ];
  })();
  const hasDurationData = durationBarData.some((d) => d.minutes > 0);

  // Crédits consommés / remboursés / nets
  const creditBarData = (() => {
    if (!resStats) return [];
    return [
      {
        key: "debited",
        name: t("adminStats.credits.debited", {
          defaultValue: "Debited (used)",
        }),
        minutes: resStats.total_debited_minutes || 0,
      },
      {
        key: "refunded",
        name: t("adminStats.credits.refunded", {
          defaultValue: "Refunded",
        }),
        minutes: resStats.total_refunded_minutes || 0,
      },
      {
        key: "net",
        name: t("adminStats.credits.net", {
          defaultValue: "Net consumed",
        }),
        minutes: resStats.net_consumed_minutes || 0,
      },
    ];
  })();
  const hasCreditData = creditBarData.some((d) => d.minutes > 0);

  return (
    <Box sx={pageWrap}>
      {/* Header + filtres */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            {t("adminStats.title", { defaultValue: t('admin.labs_overview_title') })}
          </Typography>
          {cacheBadge && (
            <Chip
              size="small"
              label={`${t("common.cache")}: ${cacheBadge}`}
            />
          )}
          {filtersActive && (
            <Chip
              size="small"
              color="primary"
              label={t("common.activeFilters")}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label={t("common.periodDays")}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ width: 140 }}
          >
            {daysOptions.map((d) => (
              <MenuItem key={d} value={d}>
                {t("common.daysN", { n: d })}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t("common.lab")}
            value={labId}
            onChange={(e) => setLabId(e.target.value)}
            sx={{ width: 220 }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {labsOptions.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label={t("adminStats.filters.user", {
              defaultValue: "User (id / login)",
            })}
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            sx={{ width: 220 }}
          />

          <TextField
            select
            size="small"
            label={t("common.status")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 200 }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`status.${s}`, s)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            label={t("common.from")}
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <TextField
            type="date"
            size="small"
            label={t("common.to")}
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />

          <Button variant="outlined" onClick={handleExport}>
            {t("common.exportCsv")}
          </Button>
        </Stack>
      </Stack>

      {loading && (
        <Box
          sx={{
            py: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!!error && (
        <Card
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor: "error.light",
          }}
        >
          <CardContent>
            <Typography color="error" fontWeight={600}>
              {t("common.error")}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {!loading && !error && (
        <>
          {/* KPIs globaux + synthèse complétées/annulées */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.kpis.totalReservations", {
                      defaultValue: "Total reservations",
                    })}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {numberFmt(kpi.total_reservations)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.kpis.todayAnd7d", {
                      defaultValue: "Today / last 7 days",
                    })}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {numberFmt(kpi.reservations_today)} /{" "}
                    {numberFmt(kpi.reservations_last_7d)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.kpis.thisMonth", {
                      defaultValue: "This month",
                    })}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {numberFmt(kpi.reservations_this_month)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* Carte synthèse complétées vs annulées + durées */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.completionCard.title", {
                      defaultValue: "Completed vs cancelled",
                    })}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {t("adminStats.completionCard.total", {
                      total: numberFmt(resStats?.total_reservations || 0),
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.completionCard.completed", {
                      completed: numberFmt(
                        resStats?.total_completed || 0
                      ),
                      cancelled: numberFmt(
                        resStats?.total_cancelled || 0
                      ),
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("adminStats.completionCard.minutes", {
                      totalMinutes: numberFmt(
                        resStats?.total_duration_minutes || 0
                      ),
                      avgMinutes: numberFmt(
                        resStats?.avg_duration_minutes || 0
                      ),
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("adminStats.completionCard.rate", {
                      rate: completionRate.toFixed(0),
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Revenue WooCommerce + Realtime ─────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 2 }}>

            {/* CA mois en cours */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderLeft: "4px solid #22c55e" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Revenus ce mois (WooCommerce)
                  </Typography>
                  {revenueLoading ? (
                    <CircularProgress size={20} sx={{ mt: 1 }} />
                  ) : (
                    <>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {revenue?.woocommerce?.total_eur != null
                          ? `${revenue.woocommerce.total_eur.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`
                          : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {revenue?.woocommerce?.total_xof != null
                          ? `≈ ${numberFmt(revenue.woocommerce.total_xof)} XOF`
                          : ""}
                        {revenue?.woocommerce?.source === "error" && (
                          <Chip size="small" label="WC indisponible" color="warning" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Commandes WC */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Commandes ({days}j)
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {revenueLoading ? "…" : numberFmt(revenue?.woocommerce?.orders_count)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {numberFmt(revenue?.labs_credits_sold_minutes)} min crédits vendus
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Sessions actives live */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderLeft: `4px solid ${realtime?.active_sessions > 0 ? "#3b82f6" : "#6b7280"}` }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Sessions actives
                    </Typography>
                    {realtime?.active_sessions > 0 && (
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        bgcolor: "#22c55e",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.4 },
                        }
                      }} />
                    )}
                  </Stack>
                  <Typography variant="h5" fontWeight={700}>
                    {realtime?.active_sessions ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {realtime?.active_users ?? "?"} users · refresh 30s
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Nouveaux users aujourd'hui + wallets expirant */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Nouveaux users / Wallets J-7
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {realtime?.new_users_today ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {realtime?.wallets_expiring_7d ?? "?"} wallets expirent dans 7j
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Graphique CA WooCommerce par jour */}
          {revenue?.timeseries?.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Revenus WooCommerce — {days} derniers jours (EUR)
                </Typography>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenue.timeseries}>
                      <defs>
                        <linearGradient id="gradEur" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(val) =>
                          [`${val.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`, "CA"]
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="total_eur"
                        stroke="#22c55e"
                        strokeWidth={2}
                        fill="url(#gradEur)"
                        name="CA (€)"
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Pie complétées vs annulées + durées + crédits */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Pie */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    {t("adminStats.pie.title", {
                      defaultValue:
                        "Distribution of completed vs cancelled",
                    })}
                  </Typography>
                  {completionPieData.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("adminStats.pie.empty", {
                        defaultValue:
                          "Not enough data to display the distribution.",
                      })}
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer
                          width="100%"
                          height="100%"
                        >
                          <PieChart>
                            <Pie
                              data={completionPieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label
                              onClick={(_, index) => {
                                const item =
                                  completionPieData[index];
                                if (!item) return;
                                if (item.key === "completed") {
                                  setStatusFilter("completed");
                                } else if (item.key === "cancelled") {
                                  setStatusFilter("cancelled");
                                } else {
                                  setStatusFilter("");
                                }
                              }}
                            >
                              {completionPieData.map(
                                (entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      PIE_COLORS[
                                        index % PIE_COLORS.length
                                      ]
                                    }
                                  />
                                )
                              )}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {t("adminStats.pie.note", {
                          defaultValue:
                            "Chart based on number of reservations per status.",
                        })}{" "}
                        {t("adminStats.pie.interaction", {
                          defaultValue:
                            "Click a slice to filter by status.",
                        })}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Durées */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    {t("adminStats.duration.title", {
                      defaultValue: "Duration by status (minutes)",
                    })}
                  </Typography>
                  {!hasDurationData ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("adminStats.duration.empty", {
                        defaultValue:
                          "Not enough duration data to display.",
                      })}
                    </Typography>
                  ) : (
                    <Box sx={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart data={durationBarData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="minutes"
                            name={t("common.minutes")}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Crédits */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    {t("adminStats.credits.title", {
                      defaultValue:
                        "Credits used vs refunded (minutes)",
                    })}
                  </Typography>
                  {!hasCreditData ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("adminStats.credits.empty", {
                        defaultValue:
                          "No credit transactions recorded yet.",
                      })}
                    </Typography>
                  ) : (
                    <Box sx={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart data={creditBarData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="minutes"
                            name={t("common.minutes")}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Séries temporelles globales */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                {t("adminStats.series.title", {
                  days,
                  defaultValue:
                    "Reservations over the last {{days}} days",
                })}
              </Typography>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="reservations"
                      name={t("common.reservations")}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Top labs + Top users */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Top labs (drill-down sur lab) */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t("adminStats.topLabs.title", {
                      defaultValue: "Top labs by usage",
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    {t("adminStats.topLabs.hintClick", {
                      defaultValue:
                        "Click a bar to filter the dashboard on this lab.",
                    })}
                  </Typography>
                  <Box sx={{ height: 260, mt: 1 }}>
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <BarChart data={topLabs}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="lab_name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="reservations"
                          name={t("common.reservations")}
                          cursor="pointer"
                          onClick={(_, index) => {
                            const lab = topLabs[index];
                            if (lab) {
                              setLabId(lab.lab_id || lab.id);
                            }
                          }}
                        />
                        <Bar
                          dataKey="minutes_booked"
                          name={t("common.minutes")}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Top users (drill-down sur user) */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t("adminStats.topUsers.title", {
                      defaultValue: "Top users by usage",
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    {t("adminStats.topUsers.hintClick", {
                      defaultValue:
                        "Click a bar to filter on this user.",
                    })}
                  </Typography>
                  <Box sx={{ height: 260, mt: 1 }}>
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <BarChart data={topUsers}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="username" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="reservations"
                          name={t("common.reservations")}
                          cursor="pointer"
                          onClick={(_, index) => {
                            const u = topUsers[index];
                            if (!u) return;
                            // à adapter selon ce que renvoie ton backend : id, username, email...
                            setUserFilter(
                              u.user_id?.toString() ||
                                u.username ||
                                ""
                            );
                          }}
                        />
                        <Bar
                          dataKey="minutes_booked"
                          name={t("common.minutes")}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Activité récente */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("adminStats.activity.title", {
                  defaultValue: "Recent activity (platform)",
                })}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {activity.map((a, idx) => (
                  <Box key={idx}>
                    <Typography variant="body2">
                      <b>{a.level}</b> — {a.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      id: {a.id} •{" "}
                      {a.created_at
                        ? new Date(a.created_at).toLocaleString()
                        : "—"}{" "}
                      • user: {a.user_id ?? "?"}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                  </Box>
                ))}
                {!activity.length && (
                  <Typography variant="body2" color="text.secondary">
                    {t("common.none")}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
